import { Injectable } from '@angular/core';
import { retry, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { ENV } from '@app/env';
import 'rxjs/add/operator/toPromise';
import { UtilitiesProvider } from './utilities';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/operators/map';

export const _TRACKING_ACTIONS = {
  VIEW: 'VIEW',
  ADD_TO_CART: 'ADD_TO_CART',
  ASSIST: 'ASSIST',
  REMOVE: 'REMOVE',
};
export const _TRACKING_ENTITIES = {
  EVENT: 'EVENT',
  CATALOG: 'CATALOG',
  STAFF: 'STAFF',
  NEWS: 'NEWS',
  SPONSOR: 'SPONSOR',
  USER_EVENTS: 'USER_EVENTS',
  COMMERCIAL_SAMPLE: 'COMMERCIAL_SAMPLE',
  EVENT_COMMERCIAL_SAMPLE: 'EVENT_COMMERCIAL_SAMPLE',
  COUPONS: 'COUPONS',
  RESERVE: 'RESERVE',
  TICKET: 'TICKET',
};

@Injectable()
export class TrackingData {

  constructor(
    private http: HttpClient,
    private utilities: UtilitiesProvider
  ) { }

  public trackActivity(user_id: string, entity_type: string, entity_id: string, client_id: string, action: string) {
    const entity = {
      user_id,
      entity_type,
      client_id,
      entity_id,
      action,
      created_at: new Date().getTime()
    };
    return this.http.post(`${ENV.ia_api_url}/events`, entity)
      .pipe(
        retry(3),
        map(() => this.utilities.consoleDecorator(`Tracking ${entity_type} ${action}`, entity, '#4a148c')),
        catchError(err => [this.utilities.consoleDecorator('Error tracking: ', err)])
      ).toPromise();
  }

  public findCatalogByUser(client_id: string, user_id: string, limit = 5): Observable<any> {
    return this.http.get(`${ENV.ia_api_url}/recommendations?client_id=${client_id}&user_id=${user_id}&k=${limit}&type=catalog`)
      .pipe(map((data: any) => {
        if (!data.data.weight) {
          data.data.weight = 0;
        }
        this.utilities.consoleDecorator('Finding catalog data by user', data, '#4a148c')
        return data;
      }), catchError((error) => {
        this.utilities.consoleError('Find Catalog by User', error);
        return Observable.of({ data: { weight: 0, count: 0 } })
      }))

  }

  public findEntityById(client_id: string, user_id: string, entity_id: string, type: 'catalog' | 'event' | 'commercial_sample' | 'event_commercial_sample' | 'coupons'): Observable<any> {
    return this.http.get(`${ENV.ia_api_url}/match?client_id=${client_id}&user_id=${user_id}&entity_id=${entity_id}&type=${type}`)
      .pipe(map((data: any) => {
        if (!data.data.weight) {
          data.data.weight = 0;
        }
        this.utilities.consoleDecorator(`Finding ${type} data by id`, data, '#4a148c')
        return data;
      }), catchError((error) => {
        this.utilities.consoleError('`Finding ${type} data by id', error);
        return Observable.of({ data: { weight: 0, count: 0 } })
      }))
  }

  public findEventByUser(client_id: string, user_id: string, limit = 5) {
    return this.http.get(`${ENV.ia_api_url}/recommendations?client_id=${client_id}&user_id=${user_id}&k=${limit}&type=event`)
      .pipe(map((data: any) => {
        if (!data.data.weight) {
          data.data.weight = 0;
        }
        this.utilities.consoleDecorator('Finding event data by user', data, '#4a148c')
        return data;
      }), catchError((error) => {
        this.utilities.consoleError('Finding event data by user', error);
        return Observable.of({ data: { weight: 0, count: 0 } })
      })).do(result => this.utilities.consoleDecorator('Finding event data by user', result, '#4a148c'));
  }

  public findCouponsByUser(client_id: string, user_id: string, limit = 5) {
    return this.http.get(`${ENV.ia_api_url}/recommendations?client_id=${client_id}&user_id=${user_id}&k=${limit}&type=coupons`)
      .pipe(map((data: any) => {
        if (!data.data.weight) {
          data.data.weight = 0;
        }
        this.utilities.consoleDecorator('Finding coupons data by user', data, '#4a148c')
        return data;
      }), catchError((error) => {
        this.utilities.consoleError('Finding coupons data by user', error);
        return Observable.of({ data: { weight: 0, count: 0 } })
      })).do(result => this.utilities.consoleDecorator('Finding coupons data by user', result, '#4a148c'));
  }

  public findCommercialByUser(client_id: string, user_id: string, limit = 5) {
    return this.http.get(`${ENV.ia_api_url}/recommendations?client_id=${client_id}&user_id=${user_id}&k=${limit}&type=commercial_sample`)
      .pipe(map((data: any) => {
        if (!data.data.weight) {
          data.data.weight = 0;
        }
        this.utilities.consoleDecorator('Finding commercial sample data by user', data, '#4a148c')
        return data;
      }), catchError((error) => {
        this.utilities.consoleError('Finding commercial sample data by user', error);
        return Observable.of({ data: { weight: 0, count: 0 } })
      })).do(result => this.utilities.consoleDecorator('Finding commercial sample data by user', result, '#4a148c'));
  }

  public findEventCommercialByUser(client_id: string, user_id: string, limit = 5) {
    return this.http.get(`${ENV.ia_api_url}/recommendations?client_id=${client_id}&user_id=${user_id}&k=${limit}&type=event_commercial_sample`)
      .pipe(map((data: any) => {
        if (!data.data.weight) {
          data.data.weight = 0;
        }
        this.utilities.consoleDecorator('Finding event commercial sample data by user', data, '#4a148c')
        return data;
      }), catchError((error) => {
        this.utilities.consoleError('Finding event commercial sample data by user', error);
        return Observable.of({ data: { weight: 0, count: 0 } })
      })).do(result => this.utilities.consoleDecorator('Finding event commercial sample data by user', result, '#4a148c'));
  }

  public trackEntity(entity_type: string, entity_id: string, client_id: string, payload: any, type: 'create' | 'update'): Promise<any> {
    let payloadTemp = { ...payload }

    switch (type) {
      case 'create':
        payloadTemp['created_at'] = new Date().getTime();
        break;
      case 'update':
        payloadTemp['updated_at'] = new Date().getTime();
        break;
    }

    const entity = {
      entity_type,
      entity_id,
      client: client_id,
      payload: payloadTemp,
      timestamp: new Date().getTime()
    };
    this.utilities.consoleDecorator('Tracking entity: ', entity);
    return this.http.post(`${ENV.ia_api_url}/records`, entity)
      .pipe(retry(3), catchError(err => [this.utilities.consoleDecorator('Error tracking: ', err)])).toPromise();
  }

}



// WEBPACK FOOTER //
// ./src/providers/tracking.ts